<template>
  <InitializingSpinner v-if="!isPlayer && !isNotFound && !isLocked" />

  <PlayerView v-if="isPlayer" is-detail-view />
  <ProfileLock v-else-if="isLocked" presentation="page" :username="lockedUsername" :unlocks="lockUnlocks" />
  <NotFound v-if="isNotFound" :title="localize('profile.not_found.title')" :username="String(route.params.username)" />
</template>

<script lang="ts" setup>
defineOptions({ name: 'UsernameView' });

import { RouterAuthType, RouterNameType } from '@/core/data/config/uiConfig.interface';
import { gambits, localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import InitializingSpinner from '@/views/components/InitializingSpinner.vue';
import { onActivated, onDeactivated, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PlayerView from './main/players/PlayerView.vue';
import {
  isHandshakeRequired,
  ProfileUnlock,
  profileUnlocks,
  type ProfileUnlock as ProfileUnlockName,
} from './main/players/profile-access';
import ProfileLock from './main/players/ProfileLock.vue';
import NotFound from './NotFound.vue';

const isPlayer = ref<boolean>(false);
const isNotFound = ref<boolean>(false);
const isLocked = ref<boolean>(false);
const lockedUsername = ref<string>('');
const lockUnlocks = ref<ProfileUnlockName[]>([ProfileUnlock.Handshake]);
const lastResolvedUsername = ref<string | null>(null);

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

async function resolveUsername(username: string) {
  if (username === lastResolvedUsername.value && !isLocked.value) return;
  const wasAlreadyPlayer = isPlayer.value;
  lastResolvedUsername.value = username;

  const isOwnProfile = authStore.userDetails?.username === username;
  if (!wasAlreadyPlayer || !isOwnProfile) {
    isPlayer.value = false;
  }
  isNotFound.value = false;
  isLocked.value = false;

  try {
    const { data, error } = await gambits.userService.getUsername(username);

    if (lastResolvedUsername.value !== username) return;

    if (error || !data) {
      isPlayer.value = false;
      isNotFound.value = true;
      return;
    }

    if (data.user && !authStore.isAuthenticated()) {
      router.push({ name: RouterAuthType.AUTH_SELECT });
    } else if (data.user && authStore.isAuthenticated()) {
      const handle = data.user.username || username;
      const isOwnProfile = authStore.userDetails?.username === handle;

      if (isOwnProfile || authStore.isAdmin()) {
        isPlayer.value = true;
      } else {
        const profile = await gambits.userService.getUserByName(handle);
        if (lastResolvedUsername.value !== username) return;

        if (isHandshakeRequired(profile.error)) {
          isPlayer.value = false;
          lockedUsername.value = handle;
          lockUnlocks.value = profileUnlocks(profile.error);
          isLocked.value = true;
        } else if (profile.error || !profile.data) {
          isPlayer.value = false;
          isNotFound.value = true;
        } else {
          isPlayer.value = true;
        }
      }
    }

    if (data.error) {
      isPlayer.value = false;
      isNotFound.value = true;
    }
  } catch (e) {
    if (lastResolvedUsername.value !== username) return;
    // eslint-disable-next-line
    console.error(`Error fetching username. (${e})`);
    isPlayer.value = false;
    isNotFound.value = true;
  }
}

onDeactivated(() => {
  // Keep lastResolvedUsername so re-activating the same profile
  // skips the destructive teardown and lets PlayerDetail.onActivated
  // handle a silent refresh instead.
});

onActivated(() => {
  const username = route.params.username;
  if (username && route.name === RouterNameType.USERNAME) {
    resolveUsername(String(username));
  }
});

watch(
  [() => route.params.username, () => route.name],
  ([username, name]) => {
    if (username && name === RouterNameType.USERNAME) {
      resolveUsername(String(username));
    }
  },
  { immediate: true },
);
</script>
